import { useState, useEffect, useCallback } from 'react'
import { getBrokerApplicationDashboard, getBrokersList } from 'actions/brokerDashboard'

/**
 * Custom hook for fetching broker application dashboard data
 * @param {Object} filters - Filter parameters
 * @param {string} filters.dateRange - Date range filter (e.g., 'last30')
 * @param {string} filters.brokerFilter - Broker filter type ('all' or 'list')
 * @param {string} filters.brokerEmails - Comma-separated broker emails (when brokerFilter is 'list')
 * @param {boolean} demoMode - Demo mode flag
 * @returns {Object} Dashboard data, loading state, error, and refetch function
 */
export const useBrokerApplicationDashboard = (filters = {}, demoMode = false) => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            
            const query = {
                dateRange: filters.dateRange || 'last30',
                brokerFilter: filters.brokerFilter || 'all',
                brokerEmails: filters.brokerEmails || ''
            }
            
            const fetchDashboard = getBrokerApplicationDashboard(demoMode, query)
            const response = await fetchDashboard()
            setData(response?.response || response)
        } catch (err) {
            setError(err)
        } finally {
            setLoading(false)
        }
    }, [filters.dateRange, filters.brokerFilter, filters.brokerEmails, demoMode])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    return {
        data,
        loading,
        error,
        refetch: fetchData
    }
}

/**
 * Custom hook for fetching brokers list
 * @returns {Object} Brokers list, loading state, and error
 */
export const useBrokersList = () => {
    const [brokers, setBrokers] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                setLoading(true)
                const response = await getBrokersList()
                setBrokers(response || [])
            } catch (err) {
                setError(err)
            } finally {
                setLoading(false)
            }
        }
        fetchBrokers()
    }, [])

    return {
        brokers,
        loading,
        error
    }
}

export default useBrokerApplicationDashboard
